/**
 * Readiness helpers for the global Mapbox libraries.
 *
 * mapbox-gl / mapbox-sdk are loaded as deferred scripts in public/index.html,
 * so they are NOT guaranteed to exist when the app bundle executes or when
 * React hydrates. public/index.html defines `window.mapboxReady`, a promise
 * that resolves (with window.mapboxgl, or null on load failure) once the
 * deferred scripts have run — always before DOMContentLoaded.
 *
 * Components must gate map rendering/initialization on this readiness instead
 * of reading `window.mapboxgl` synchronously at mount time.
 */
import { useEffect, useState } from 'react';

import config from '../config';

export const isMapsLibLoaded = () => typeof window !== 'undefined' && !!window.mapboxgl;

const ensureAccessToken = (mapboxgl) => {
  if (mapboxgl && !mapboxgl.accessToken) {
    mapboxgl.accessToken = config.maps.mapboxAccessToken;
  }
  return mapboxgl || null;
};

/**
 * Resolves with `window.mapboxgl` (access token guaranteed to be set) once the
 * deferred Mapbox scripts have executed, or with `null` when Mapbox could not
 * be loaded — callers keep whatever fallback UI they render.
 *
 * @returns {Promise<Object|null>}
 */
export const waitForMapsLib = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (isMapsLibLoaded()) return Promise.resolve(ensureAccessToken(window.mapboxgl));
  if (window.mapboxReady && typeof window.mapboxReady.then === 'function') {
    return window.mapboxReady.then(ensureAccessToken);
  }
  // No readiness promise (tests, storybook, legacy HTML): report what exists.
  return Promise.resolve(ensureAccessToken(window.mapboxgl));
};

/**
 * Reactive variant of isMapsLibLoaded: re-renders the component once the
 * Mapbox libraries become available, so render-time gates don't get stuck on
 * the fallback branch when hydration wins the race against the deferred
 * scripts.
 *
 * Deliberately starts as `false` even when the lib is already present: the
 * server-rendered HTML was produced with `false`, so the first client render
 * must match it (no hydration mismatch). The flip happens in the mount effect.
 *
 * @returns {boolean}
 */
export const useMapsLibLoaded = () => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    waitForMapsLib().then((mapboxgl) => {
      if (!cancelled && mapboxgl) setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return loaded;
};
